/**
 * Place Overview — Frontend ローカル Contract
 *
 * BE 側 `dresscode-backend/v2/src/ga-force/api/place-overview/usecase/place-overview.api-contract.ts`
 * と同じ Zod 構造を再現。BE が `@standandforce/api-contract` に publish された後、
 * client.ts でローカル import を置き換える想定。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';

export const PlaceStatusValueSchema = z.enum([
  'ACTIVE',
  'PLANNED',
  'UNDER_CONSTRUCTION',
  'TEMPORARILY_CLOSED',
  'UNDER_RENOVATION',
  'PLANNED_CLOSURE',
  'CLOSED',
  'SUSPENDED',
]);

export const PlaceOverviewCountByStatusSchema = z.object({
  total: z.number(),
  active: z.number(),
  planned: z.number(),
  temporarilyClosed: z.number(),
  closed: z.number(),
  byStatus: z.array(
    z.object({
      status: PlaceStatusValueSchema,
      count: z.number(),
    }),
  ),
});

const PlaceOverviewAttributeItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  count: z.number(),
});

export const PlaceOverviewCountByAttributeSchema = z.object({
  byBusinessPlaceType: z.array(PlaceOverviewAttributeItemSchema),
  byOwnershipType: z.array(PlaceOverviewAttributeItemSchema),
  byWorkStyle: z.array(PlaceOverviewAttributeItemSchema),
});

const PlaceOverviewTimeSeriesPointSchema = z.object({
  month: z.string(),
  cumulativeCount: z.number(),
  addedCount: z.number(),
});
export const PlaceOverviewTimeSeriesSchema = z.object({
  monthly: z.array(PlaceOverviewTimeSeriesPointSchema),
});

export const PlaceOverviewActivityTypeSchema = z.enum(['ADDED']);
const PlaceOverviewActivityItemSchema = z.object({
  placeId: z.string(),
  placeName: z.string(),
  activityType: PlaceOverviewActivityTypeSchema,
  occurredAt: z.string(),
});
export const PlaceOverviewActivitySchema = z.object({
  recent: z.array(PlaceOverviewActivityItemSchema),
});

export const placeOverviewContract = oc.router({
  countByStatus: oc
    .route({ method: 'GET', path: '/v2/place-overview/count-by-status' })
    .output(PlaceOverviewCountByStatusSchema),
  countByAttribute: oc
    .route({ method: 'GET', path: '/v2/place-overview/count-by-attribute' })
    .output(PlaceOverviewCountByAttributeSchema),
  timeSeries: oc
    .route({ method: 'GET', path: '/v2/place-overview/time-series' })
    .output(PlaceOverviewTimeSeriesSchema),
  recentActivity: oc
    .route({ method: 'GET', path: '/v2/place-overview/recent-activity' })
    .output(PlaceOverviewActivitySchema),
});

export type PlaceStatusValue = z.infer<typeof PlaceStatusValueSchema>;
export type PlaceOverviewCountByStatus = z.infer<
  typeof PlaceOverviewCountByStatusSchema
>;
export type PlaceOverviewCountByAttribute = z.infer<
  typeof PlaceOverviewCountByAttributeSchema
>;
export type PlaceOverviewAttributeItem = z.infer<
  typeof PlaceOverviewAttributeItemSchema
>;
export type PlaceOverviewTimeSeries = z.infer<
  typeof PlaceOverviewTimeSeriesSchema
>;
export type PlaceOverviewActivity = z.infer<typeof PlaceOverviewActivitySchema>;
export type PlaceOverviewActivityItem = z.infer<
  typeof PlaceOverviewActivityItemSchema
>;
